import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { AppDispatch } from '@europrocurement/flexy-components/redux/storeConfig/store';
import { FactureAchatFactureAchatWrite } from '@europrocurement/l2d-domain/openApi/ApiAchats';
import { useAchatsService, useSociete } from '@europrocurement/l2d-domain';
import { FactureFormObject } from '../pages/Achats/components/forms/types';
import { useSynchroG3Facture } from '../pages/Achats/components/forms/functions/dataHooks';

export type UpdatePublisherProps = {
    invoice: { id?: number | string };
    newPublisher: Pick<
        FactureFormObject,
        | 'fournisseur'
        | 'siren_fournisseur'
        | 'raison_sociale_fournisseur'
        | 'type_remise_fournisseur'
    >;
    isAlreadySyncG3?: boolean;
};

/**
 * Custom hook to calculate and update invoice publisher.
 */
const useSectionPublisher = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { enqueueSnackbar } = useSnackbar();

    const xIdSociete = useSociete();
    const { selectFactureAchat, factureAchatApi } = useAchatsService();

    const synchroG3Facture = useSynchroG3Facture();

    /**
     * Updates the publisher on the server.
     * @param newPublisher - The publisher to update.
     */
    const updatePublisher = useCallback(
        async ({ invoice, newPublisher, isAlreadySyncG3 = false }: UpdatePublisherProps) => {
            const payload: Pick<
                FactureAchatFactureAchatWrite,
                'idEntiteFacturante' | 'rcsEntiteFacturante'
            > = {};

            if (typeof newPublisher.siren_fournisseur !== 'string') {
                payload.idEntiteFacturante = newPublisher.siren_fournisseur?.id;
                payload.rcsEntiteFacturante = newPublisher.siren_fournisseur?.siren;
            } else {
                console.error(
                    'Publisher autocomplete value is a string, try to get id by another way, but certainly fail',
                );
                payload.idEntiteFacturante = newPublisher.fournisseur?.id;
                payload.rcsEntiteFacturante = newPublisher.siren_fournisseur;
            }

            const invoiceRequest = () =>
                factureAchatApi.patchFactureAchatFactureAchatItem({
                    id: invoice.id?.toString() || '-1',
                    factureAchatFactureAchatWrite: {
                        idEntiteFacturante: payload.idEntiteFacturante,
                        rcsEntiteFacturante: payload.rcsEntiteFacturante,
                        libelleFacturante: newPublisher.raison_sociale_fournisseur,
                        hasSetRsf: newPublisher.type_remise_fournisseur !== 'Pas de remise',
                    },
                    xIdSociete,
                });

            const syncRequest = () => synchroG3Facture(invoice.id?.toString() || '-1');

            const requestPromiseList: Array<typeof invoiceRequest> = [];

            requestPromiseList.push(invoiceRequest);

            try {
                await Promise.all(requestPromiseList.map((req) => req()));

                if (isAlreadySyncG3) {
                    syncRequest();
                }

                if (invoice.id) {
                    dispatch(selectFactureAchat({ id: invoice.id }));
                }

                enqueueSnackbar(`Le fournisseur de la facture à été mis à jour avec succès`);
            } catch (error) {
                console.error('Error updating publisher fields:', error);

                enqueueSnackbar(
                    `Une erreur est survenue lors de la mise à jour du fournisseur de la facture`,
                    {
                        variant: 'error',
                    },
                );
            }
        },
        [
            dispatch,
            enqueueSnackbar,
            factureAchatApi,
            selectFactureAchat,
            synchroG3Facture,
            xIdSociete,
        ],
    );

    return {
        updatePublisher,
    };
};

export default useSectionPublisher;
